import {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {useGoogleOneTapLogin} from "@react-oauth/google";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {authSucceeded} from "../actions/auth_succeeded_event";
import {googleOneTapLogin} from "../api/google_one_tap_login";
import {authAnalytics, GAAuthEventCategory, GAAuthEventName} from "../tracking/auth_analytics";
import {AuthActionType} from "../types/AuthActionType";

interface IProps {
    disable?: boolean;
    gtmSourceSection?: string;
    userSource?: UserSource;
}

const ONE_TAP_DELAY_MS = 20000;

export const useGoogleOneTap = (props: IProps = {}) => {
    const isAuthenticated = useIsAuthenticated();
    const dispatch = useDispatch();
    const [readyToShow, setReadyToShow] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setReadyToShow(true), ONE_TAP_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    const onOneTapLoginSuccess = async (isNewUser: boolean) => {
        const userSource = props.userSource ?? UserSource.NAVIGATION;

        authAnalytics.gtm.googleAuthEvent({
            event: isNewUser ? GAAuthEventName.SIGN_UP : GAAuthEventName.LOGIN,
            eventCategory: GAAuthEventCategory.GOOGLE_ONE_TAP,
            source: props.gtmSourceSection ?? null,
            userSource
        });

        dispatch(
            authSucceeded({
                type: isNewUser ? AuthActionType.REGISTRATION : AuthActionType.LOGIN,
                userSource,
                registrationMethod: "social"
            })
        );
    };

    const googleOnOneTapLoginError = (error?: unknown) => {
        notifyBugsnag({message: `One tap login error`}, `third-party-login error`, {originalError: error});

        dispatch(addFailureNotification("Coś poszło nie tak. Spróbuj później."));
    };

    useGoogleOneTapLogin({
        onSuccess: async (response) => {
            if (response.credential) {
                await googleOneTapLogin({
                    credential: response.credential,
                    onSuccess: onOneTapLoginSuccess,
                    onError: googleOnOneTapLoginError
                });
            }
        },
        onError: () => {
            googleOnOneTapLoginError("useGoogleOneTapLogin error, login in failure");
        },
        disabled: isAuthenticated || props.disable || !readyToShow
    });
};
